/**
 * Election detail — phase-driven, one surface for three audiences:
 *  member    stand for election (NOMINATIONS), cast a secret ballot
 *            (VOTING, self-run; tallies hidden until declared), see results
 *  officer   advance phases, scrutinise nominations (rejections pre-fill from
 *            the eligibility engine's citation), declare results, cancel
 *  admin     constitute the committee — winners join the Committee Member
 *            position, wiring straight into meetings/quorum/compliance
 * Wired: GET /api/elections/<id>, POST nominate | candidates/<cid>/scrutiny |
 * vote | advance | declare | constitute | cancel.
 */
import { useLocalSearchParams } from 'expo-router';
import React from 'react';
import { Pressable, View } from 'react-native';

import { space } from '@bero/tokens';
import { goBack, SmallButton } from '@/features/account-admin/bits';
import { Icon } from '@/features/account-admin/icons';
import { useT } from '@/i18n';
import type { DictKey } from '@/i18n/en';
import { api } from '@/lib/client';
import { useSession } from '@/lib/session';
import { useTheme } from '@/theme';
import {
  AppText, Button, Card, EmptyState, Field, Input, Pill, Screen, SectionLabel,
  Sheet, Skeleton, TopBar,
} from '@/ui';

import { fmtDay, PHASE_META } from './index';

type Candidate = {
  id: number; userId: number; name: string | null; proposedByName: string | null;
  statement: string; eligibleAtNomination: boolean; eligibilityNote: string;
  scrutinyStatus: 'PENDING' | 'ACCEPTED' | 'REJECTED'; scrutinyReason: string;
  votes: number | null; elected: boolean;
};
type Detail = {
  id: number; title: string; termLabel: string; seatsTotal: number; reservedNote: string;
  mode: 'SCEA' | 'SELF_RUN'; status: string; phaseLabel: string;
  nominationClose: string | null; votingDate: string | null; resultDeclaredAt: string | null;
  candidates: Candidate[]; iVoted: boolean; amCandidate: boolean;
};

const ADVANCE_LABEL: Record<string, DictKey> = {
  ANNOUNCED: 'eld_adv_announced',
  NOMINATIONS: 'eld_adv_nominations',
  SCRUTINY: 'eld_adv_scrutiny',
};

export default function ElectionDetailScreen() {
  const { colors } = useTheme();
  const { t } = useT();
  const { user } = useSession();
  const { id } = useLocalSearchParams<{ id: string }>();
  const isAdmin = user?.role === 'SUB_ADMIN' || user?.role === 'MASTER_ADMIN';

  const [e, setE] = React.useState<Detail | null>(null);
  const [state, setState] = React.useState<'loading' | 'ready' | 'error'>('loading');
  const [bits, setBits] = React.useState<string[]>([]);
  const [busy, setBusy] = React.useState(false);
  const [notice, setNotice] = React.useState<{ kind: 'ok' | 'err'; text: string } | null>(null);

  const [standOpen, setStandOpen] = React.useState(false);
  const [statement, setStatement] = React.useState('');
  const [ballotFor, setBallotFor] = React.useState<Candidate | null>(null);
  const [declareOpen, setDeclareOpen] = React.useState(false);
  const [electedPick, setElectedPick] = React.useState<Set<number>>(new Set());

  const load = React.useCallback(async () => {
    setState('loading');
    try {
      const [d, me] = await Promise.all([
        api.get<Detail>(`/api/elections/${id}`),
        api.get<{ perms?: Record<string, string[]> }>('/api/positions/me').catch(() => ({ perms: {} as Record<string, string[]> })),
      ]);
      setE(d);
      setBits(me.perms?.['governance.elections'] ?? []);
      setState('ready');
    } catch {
      setState('error');
    }
  }, [id]);
  React.useEffect(() => { void Promise.resolve().then(() => load()); }, [load]);

  const post = (path: string, body: object, okMsg: string) => {
    setBusy(true);
    setNotice(null);
    api.post<unknown>(`/api/elections/${id}/${path}`, body)
      .then(() => { setNotice({ kind: 'ok', text: okMsg }); void load(); })
      .catch((er) => {
        const x = er as { error?: string; citation?: string };
        setNotice({ kind: 'err', text: x?.error ? (x.citation ? `${x.error} — ${x.citation}` : x.error) : t('eld_err_generic') });
      })
      .finally(() => {
        setBusy(false);
        setStandOpen(false);
        setBallotFor(null);
        setDeclareOpen(false);
      });
  };

  if (state === 'loading' || !e) {
    return (
      <Screen scroll={false}>
        <TopBar title={t('eld_title')} onBack={goBack} />
        <Screen padded style={{ paddingTop: space.s4 }}>
          {state === 'error' ? (
            <EmptyState icon={<Icon name="alert-circle" size={40} color={colors.textTertiary} />}
              title={t('eld_err_load')} cta={t('retry')} onCta={() => { void load(); }} />
          ) : <View style={{ gap: space.s3 }}>{[0, 1, 2].map((i) => <Skeleton key={i} h={84} />)}</View>}
        </Screen>
      </Screen>
    );
  }

  const ph = PHASE_META[e.status];
  const canEdit = bits.includes('edit');
  const accepted = e.candidates.filter((c) => c.scrutinyStatus === 'ACCEPTED');
  const inScrutinyWindow = e.status === 'NOMINATIONS' || e.status === 'SCRUTINY';

  const metaParts = [t(e.seatsTotal === 1 ? 'eld_seat_one' : 'eld_seat_other', { n: e.seatsTotal })];
  if (e.termLabel) metaParts.push(t('eld_term', { t: e.termLabel }));
  metaParts.push(e.mode === 'SCEA' ? 'SCEA-run' : 'self-run ballot');
  if (e.nominationClose) metaParts.push(t('eld_nom_close', { d: fmtDay(e.nominationClose) }));
  if (e.votingDate) metaParts.push(t('eld_polls', { d: fmtDay(e.votingDate) }));

  return (
    <Screen scroll={false}>
      <TopBar title={t('eld_title')} onBack={goBack} />
      <Screen padded style={{ paddingTop: space.s4 }}>
        <View style={{ flexDirection: 'row', alignItems: 'flex-start', gap: space.s2 }}>
          <AppText variant="headingMd" weight="600" style={{ flex: 1 }}>{e.title}</AppText>
          <Pill status={ph?.s ?? 'pending'} label={ph ? t(ph.labelKey) : e.status} />
        </View>
        <AppText variant="bodySm" tone="tertiary" style={{ marginTop: space.s2 }}>
          {metaParts.join(' · ')}
        </AppText>
        {e.reservedNote ? (
          <View style={{ flexDirection: 'row', gap: 6, marginTop: space.s2, alignItems: 'flex-start' }}>
            <Icon name="lock" size={12} color={colors.accent700} />
            <AppText variant="bodySm" style={{ color: colors.accent700, flex: 1 }}>
              {e.reservedNote}
            </AppText>
          </View>
        ) : null}

        {notice ? (
          <AppText variant="bodySm" weight="500"
            style={{ marginTop: space.s3, color: notice.kind === 'ok' ? colors.primary700 : colors.danger500 }}>
            {notice.text}
          </AppText>
        ) : null}

        {/* member actions */}
        {e.status === 'NOMINATIONS' && !e.amCandidate ? (
          <Button title={t('eld_stand')} style={{ marginTop: space.s4 }}
            onPress={() => { setStatement(''); setStandOpen(true); }} />
        ) : null}
        {e.status === 'NOMINATIONS' && e.amCandidate ? (
          <AppText variant="bodySm" weight="500" style={{ marginTop: space.s4, color: colors.primary700 }}>
            {t('eld_nom_in')}
          </AppText>
        ) : null}
        {e.status === 'VOTING' && e.mode === 'SELF_RUN' && e.iVoted ? (
          <AppText variant="bodySm" weight="500" style={{ marginTop: space.s4, color: colors.primary700 }}>
            {t('eld_ballot_in')}
          </AppText>
        ) : null}

        {/* candidates */}
        <SectionLabel style={{ marginTop: space.s5 }}>
          {e.status === 'COMPLETED' ? t('eld_results') : t('eld_candidates', { n: e.candidates.length })}
        </SectionLabel>
        {e.candidates.length === 0 ? (
          <AppText variant="bodySm" tone="tertiary">{t('eld_no_noms')}</AppText>
        ) : (
          <View style={{ gap: space.s3 }}>
            {e.candidates.map((c) => (
              <Card key={c.id}>
                <View style={{ flexDirection: 'row', alignItems: 'flex-start', gap: space.s2 }}>
                  <View style={{ flex: 1, minWidth: 0 }}>
                    <AppText variant="bodyMd" weight="600">{c.name ?? t('eld_member')}</AppText>
                    {c.statement ? (
                      <AppText variant="bodySm" tone="secondary" style={{ marginTop: 2 }}>{c.statement}</AppText>
                    ) : null}
                    {c.proposedByName && c.proposedByName !== c.name ? (
                      <AppText variant="bodySm" tone="tertiary" style={{ marginTop: 2 }}>
                        {t('eld_proposed_by', { name: c.proposedByName })}
                      </AppText>
                    ) : null}
                  </View>
                  {e.status === 'COMPLETED'
                    ? (c.elected
                      ? <Pill status="approved" label={t('eld_pill_elected')} />
                      : c.scrutinyStatus === 'REJECTED'
                        ? <Pill status="rejected" label={t('eld_pill_rejected')} />
                        : null)
                    : c.scrutinyStatus === 'ACCEPTED'
                      ? <Pill status="approved" label={t('eld_pill_on_ballot')} />
                      : c.scrutinyStatus === 'REJECTED'
                        ? <Pill status="rejected" label={t('eld_pill_rejected')} />
                        : <Pill status="pending" label={t('eld_pill_pending')} />}
                </View>
                {e.status === 'COMPLETED' && e.mode === 'SELF_RUN' && c.votes != null
                  && c.scrutinyStatus === 'ACCEPTED' ? (
                    <AppText variant="bodySm" weight="500" mono style={{ marginTop: space.s2 }}>
                      {t(c.votes === 1 ? 'eld_vote_one' : 'eld_vote_other', { n: c.votes })}
                    </AppText>
                  ) : null}
                {!c.eligibleAtNomination && c.eligibilityNote && c.scrutinyStatus !== 'REJECTED' ? (
                  <View style={{ flexDirection: 'row', gap: 6, marginTop: space.s2, alignItems: 'flex-start' }}>
                    <Icon name="lock" size={12} color={colors.accent700} />
                    <AppText variant="bodySm" style={{ color: colors.accent700, flex: 1 }}>
                      {t('eld_flagged', { note: c.eligibilityNote })}
                    </AppText>
                  </View>
                ) : null}
                {c.scrutinyStatus === 'REJECTED' && c.scrutinyReason ? (
                  <AppText variant="bodySm" style={{ marginTop: space.s2, color: colors.danger500 }}>
                    {c.scrutinyReason}
                  </AppText>
                ) : null}
                {canEdit && inScrutinyWindow && c.scrutinyStatus === 'PENDING' ? (
                  <View style={{ flexDirection: 'row', gap: space.s2, marginTop: space.s3 }}>
                    <SmallButton label={t('eld_accept')} icon="check" disabled={busy}
                      onPress={() => post(`candidates/${c.id}/scrutiny`, { decision: 'ACCEPTED' }, t('eld_ok_accepted', { name: c.name ?? t('eld_member') }))} />
                    <SmallButton label={t('eld_reject')} danger disabled={busy}
                      onPress={() => post(`candidates/${c.id}/scrutiny`, { decision: 'REJECTED' }, t('eld_ok_rejected', { name: c.name ?? t('eld_member') }))} />
                  </View>
                ) : null}
                {e.status === 'VOTING' && e.mode === 'SELF_RUN' && !e.iVoted
                  && c.scrutinyStatus === 'ACCEPTED' ? (
                    <View style={{ flexDirection: 'row', marginTop: space.s3 }}>
                      <SmallButton label={t('eld_vote_for', { name: (c.name ?? t('eld_member')).split(' ')[0] })} icon="check"
                        disabled={busy} onPress={() => setBallotFor(c)} />
                    </View>
                  ) : null}
              </Card>
            ))}
          </View>
        )}

        {/* officer actions */}
        <View style={{ marginTop: space.s5, gap: space.s2, marginBottom: space.s6 }}>
          {canEdit && ADVANCE_LABEL[e.status] ? (
            <Button title={busy ? t('eld_working') : t(ADVANCE_LABEL[e.status])} disabled={busy}
              onPress={() => post('advance', {}, t('eld_ok_advanced'))} />
          ) : null}
          {canEdit && e.status === 'VOTING' ? (
            <Button title={t('eld_declare')} disabled={busy}
              onPress={() => {
                if (e.mode === 'SCEA') { setElectedPick(new Set()); setDeclareOpen(true); }
                else post('declare', {}, t('eld_ok_declared'));
              }} />
          ) : null}
          {isAdmin && e.status === 'COMPLETED' ? (
            <Button title={busy ? t('eld_working') : t('eld_constitute')} disabled={busy}
              onPress={() => post('constitute', {}, t('eld_ok_constituted'))} />
          ) : null}
          {canEdit && e.status !== 'COMPLETED' && e.status !== 'CANCELLED' ? (
            <Button title={t('eld_cancel_election')} kind="tertiary" disabled={busy}
              onPress={() => post('cancel', {}, t('eld_ok_cancelled'))} />
          ) : null}
        </View>
      </Screen>

      {/* stand for election */}
      <Sheet visible={standOpen} onClose={() => setStandOpen(false)}>
        <SectionLabel>{t('eld_stand')}</SectionLabel>
        <AppText variant="bodySm" tone="tertiary" style={{ marginTop: space.s2 }}>
          Your nomination is checked against the eligibility rules (active member,
          no dues beyond 3 months) and goes to scrutiny.
        </AppText>
        <View style={{ marginTop: space.s4 }}>
          <Field label={t('eld_f_statement')} help={t('eld_f_statement_help')}>
            <Input value={statement} onChangeText={setStatement} multiline maxLength={500}
              placeholder={t('eld_ph_statement')} />
          </Field>
        </View>
        <Button title={busy ? t('eld_submitting') : t('eld_submit_nom')} disabled={busy}
          style={{ marginTop: space.s4 }}
          onPress={() => post('nominate', { statement: statement.trim() }, t('eld_ok_nom'))} />
      </Sheet>

      {/* confirm ballot */}
      <Sheet visible={ballotFor !== null} onClose={() => setBallotFor(null)}>
        {ballotFor ? (
          <>
            <SectionLabel>{t('eld_cast_ballot')}</SectionLabel>
            <AppText variant="headingSm" weight="500" style={{ marginTop: space.s2 }}>
              {t('eld_vote_confirm', { name: ballotFor.name ?? t('eld_member') })}
            </AppText>
            <AppText variant="bodySm" tone="tertiary" style={{ marginTop: space.s2 }}>
              {t('eld_ballot_rules')}
            </AppText>
            <Button title={busy ? t('eld_casting') : t('eld_cast_btn')} disabled={busy}
              style={{ marginTop: space.s4 }}
              onPress={() => post('vote', { candidateId: ballotFor.id }, t('eld_ok_ballot'))} />
          </>
        ) : null}
      </Sheet>

      {/* SCEA declare: pick who the authority declared elected */}
      <Sheet visible={declareOpen} onClose={() => setDeclareOpen(false)}>
        <SectionLabel>Record the SCEA result</SectionLabel>
        <AppText variant="bodySm" tone="tertiary" style={{ marginTop: space.s2 }}>
          Tick the candidates the State Co-op Election Authority declared elected.
        </AppText>
        <View style={{ marginTop: space.s3 }}>
          {accepted.map((c) => {
            const on = electedPick.has(c.userId);
            return (
              <Pressable key={c.id} accessibilityRole="checkbox" accessibilityState={{ checked: on }}
                onPress={() => setElectedPick((s) => {
                  const next = new Set(s);
                  if (next.has(c.userId)) next.delete(c.userId); else next.add(c.userId);
                  return next;
                })}
                style={{ flexDirection: 'row', alignItems: 'center', gap: space.s3, paddingVertical: 10 }}>
                <View style={{
                  width: 22, height: 22, borderRadius: 6, borderWidth: 1.5,
                  borderColor: on ? colors.primary500 : colors.border,
                  backgroundColor: on ? colors.primary500 : 'transparent',
                  alignItems: 'center', justifyContent: 'center',
                }}>
                  {on ? <Icon name="check" size={13} color={colors.canvas0} /> : null}
                </View>
                <AppText variant="bodyMd">{c.name}</AppText>
              </Pressable>
            );
          })}
        </View>
        <Button title={busy ? t('eld_recording') : t('eld_record_result')} disabled={busy || electedPick.size === 0}
          style={{ marginTop: space.s4 }}
          onPress={() => post('declare', { electedUserIds: [...electedPick] }, t('eld_ok_recorded'))} />
      </Sheet>
    </Screen>
  );
}
